// ═══════════════════════════════════════════════════════════════
// Perimeter Module — Geo Utilities
// 1:1 port from perimeter-embedded.html geo functions
// ═══════════════════════════════════════════════════════════════

import {
  R_EARTH,
  R_MILES,
  DENSITY_MAP,
  DIR_ORDER,
  PRIORITY_LABELS,
  type PerimeterShape,
} from './perimeterConstants';

// ── Types ────────────────────────────────────────────────────

export interface LatLng {
  lat: number;
  lng: number;
}

export interface PopulationEstimate {
  low: number;
  high: number;
  area: string;
}

export interface PerimeterPOI {
  id: string;
  name: string;
  lat: number;
  lng: number;
  category: string;
  categoryLabel: string;
  categoryIcon: string;
  address: string;
  phone: string;
  distance: number;
  source: string;
}

export interface PerimeterClosure {
  id: string;
  name: string;
  streets: string[];
  lat: number;
  lng: number;
  distance: number;
  priority: number;
  roadType: string;
  direction: string;
  bearing: number;
}

// ── Haversine Distance (miles) ───────────────────────────────

export function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R_MILES * Math.asin(Math.sqrt(a));
}

// ── Generate Square Perimeter Corners ────────────────────────

export function generateSquarePerimeter(center: LatLng, radiusM: number): [number, number][] {
  const dLat = (radiusM / R_EARTH) * (180 / Math.PI);
  const dLng = dLat / Math.cos(center.lat * Math.PI / 180);
  return [
    [center.lat + dLat, center.lng - dLng],
    [center.lat + dLat, center.lng + dLng],
    [center.lat - dLat, center.lng + dLng],
    [center.lat - dLat, center.lng - dLng],
  ];
}

// ── Cardinal Direction from point1 → point2 ─────────────────

export function getCardinalDirection(lat1: number, lng1: number, lat2: number, lng2: number): string {
  const angle = Math.atan2(lng2 - lng1, lat2 - lat1) * 180 / Math.PI;
  const idx = Math.round(((angle + 360) % 360) / 45) % 8;
  return DIR_ORDER[idx];
}

// ── Population Estimate ──────────────────────────────────────

export function estimatePopulation(
  radius: number,
  shape: PerimeterShape,
  density: string
): PopulationEstimate {
  const area = shape === 'circle' ? Math.PI * radius * radius : 4 * radius * radius;
  const d = DENSITY_MAP[density];
  return {
    low: Math.round(area * d.low),
    high: Math.round(area * d.high),
    area: area.toFixed(2),
  };
}

// ── Export Closures as CSV ────────────────────────────────────

export function exportClosuresCSV(
  closures: PerimeterClosure[],
  address: string,
  center: LatLng | null,
  radius: number,
  shape: PerimeterShape,
  density: string,
  pois: PerimeterPOI[]
): void {
  const pop = estimatePopulation(radius, shape, density);
  let csv =
    `PERIMETER INCIDENT REPORT\n` +
    `Generated,${new Date().toISOString()}\n` +
    `Location,"${address}"\n` +
    `Center,"${center?.lat}, ${center?.lng}"\n` +
    `Radius,${radius} mi\n` +
    `Shape,${shape}\n` +
    `Density,${density}\n` +
    `Pop Estimate,"${pop.low.toLocaleString()} - ${pop.high.toLocaleString()}"\n` +
    `Area,${pop.area} sq mi\n` +
    `\n` +
    `POINTS OF INTEREST\n` +
    `Name,Category,Address,Distance (mi),Lat,Lng\n`;

  pois.forEach(p => {
    csv += `"${p.name}","${p.categoryLabel}","${p.address}",${p.distance.toFixed(3)},${p.lat},${p.lng}\n`;
  });

  csv +=
    `\nROAD CLOSURES\n` +
    `Intersection,Direction,Distance (mi),Road Type,Priority,Lat,Lng\n`;

  closures.forEach(c => {
    csv += `"${c.name}",${c.direction},${c.distance.toFixed(3)},${c.roadType},${PRIORITY_LABELS[c.priority]},${c.lat},${c.lng}\n`;
  });

  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `Perimeter_${(address || 'report').replace(/[^a-z0-9]/gi, '_').substring(0, 30)}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
}

// ── Export Incident CSV (alias for backwards compat) ─────────

export const exportIncidentCSV = exportClosuresCSV;
